import { DocumentNode, gql, useQuery } from '@apollo/client';
import {
  Button,
  Center,
  Grid,
  GridItem,
  HStack,
  Image,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { convertFromRaw } from 'draft-js';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { DeprecatedDialInInfo } from '@/components/DialInInfo';
import { EventAudience } from '@/components/EventAudience';
import { EventTime } from '@/components/EventTime';
import { VenueFragment } from '@/fragments/venue-fragment';
import { ERegistrationType } from '@/graphql-types/globalTypes';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { SpeakerCard } from '@/imports/ui/components/SpeakerCard';
import { ResourceCard } from '@/imports/ui/pages/conference/pages/conference-video-pages/components/SharedResourcesCard/components/ResourceCard';

import {
  MobileEventInfo_Resources,
  MobileEventInfo_ResourcesVariables,
} from './graphql-types/MobileEventInfo_Resources';
import { SimpleEventMobileInfo_EventFragment } from './graphql-types/SimpleEventMobileInfo_EventFragment';

const INCREMENT_NUM = 3;

export const SimpleEventMobileInfo: React.FC<{
  eventData?: SimpleEventMobileInfo_EventFragment;
}> & { eventFragment: DocumentNode } = ({ eventData }) => {
  const [numfilesToShow, setNumfilesToShow] = React.useState(INCREMENT_NUM);
  const router = useRouter();
  const {
    data: resourcesData,
    loading: resourcesLoading,
    refetch: refetchResources,
  } = useQuery<MobileEventInfo_Resources, MobileEventInfo_ResourcesVariables>(
    RESOURCES_QUERY,
    {
      variables: { input: { _id: router.query.eventId.toString() } },
    }
  );

  if (!eventData || eventData?.__typename !== 'SimpleEventV2') {
    return null;
  }

  const allResources =
    resourcesData?.event.eventV2?.__typename === 'SimpleEventV2'
      ? resourcesData.event.eventV2.resources.results
      : undefined;

  const resourcesCount = allResources?.length ?? 0;

  // figure out if we can show the user the dial-in info
  const eventRegistrationType: ERegistrationType =
    eventData.registrationType ?? ERegistrationType.OPEN;

  const userRegisteredForEvent = eventData.currentValidUserRegistration?._id;
  const showUserDialInProps =
    (eventRegistrationType === ERegistrationType.TICKET &&
      userRegisteredForEvent) ||
    eventRegistrationType === ERegistrationType.OPEN ||
    eventData.canCurrentUserJoinEventEarly;

  const dialInProps =
    showUserDialInProps &&
    eventData.venue.online &&
    eventData.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
    eventData.venue.online.stream.__typename ===
      'EventVenue_TradewingPlatform_Stream_Live' &&
    eventData.venue.online.stream.virtualEvent
      ? {
          dialInCode: eventData.venue.online.stream.virtualEvent.dialInCode,
          listenerDialInCode:
            eventData.venue.online.stream.virtualEvent.listenerDialInCode,
          dialInEnabled:
            eventData.venue.online.stream.virtualEvent.dialInEnabled,
          dialInPhoneNumber:
            eventData.venue.online.stream.virtualEvent.dialInPhoneNumber,
          isCurrentUserSpeaker: eventData.isCurrentUserSpeaker,
          isParticipationAllowed:
            eventData.venue.online.stream.virtualEvent.maxParticipants > 0,
        }
      : undefined;
  const { startDate, endDate } = eventData;

  return (
    <VStack
      overflowY="scroll"
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
          display: 'none',
        },
      }}
    >
      <VStack shadow="md" spacing="1" w="full">
        <Image
          w="full"
          h="auto"
          zIndex="overlay"
          src={eventData.bannerImage?.imageProxy || ''}
          alt="event banner"
          fallback={<ImageFallback w="full" aspectRatio="8/3" bg="gray.200" />}
        />

        <Stack w="full" p="4">
          <Text>
            {startDate && endDate ? (
              <EventTime
                startDate={startDate}
                endDate={endDate}
              />
            ) : (
              'TBD'
            )}
          </Text>

          <Text noOfLines={2} fontWeight="semibold" fontSize="xl">
            {eventData.name}
          </Text>

          <EventAudience event={eventData} />
        </Stack>
      </VStack>

      {eventData.speakers?.length && (
        <VStack alignItems="flex-start" p="4" w="full" shadow="md" spacing="2">
          <Text fontWeight="semibold">
            {eventData.speakers.length > 1 ? 'Speakers' : 'Speaker'}
          </Text>

          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap="4">
            {eventData.speakers
              .filter((speaker) => !speaker.isHidden)
              .map((speaker) => (
                <NextLink
                  href={`/members/${speaker.user._id}`}
                  key={speaker.user._id}
                >
                  <GridItem>
                    <SpeakerCard user={speaker.user} />
                  </GridItem>
                </NextLink>
              ))}
          </Grid>
        </VStack>
      )}

      {eventData.description !== null &&
        convertFromRaw(eventData.description).hasText() && (
          <VStack alignItems="flex-start" p="4" w="full" shadow="md">
            <Text fontWeight="semibold">Description</Text>
            {eventData.description && (
              <AbbreviateRedraft
                rawDraftContentState={eventData.description}
                noOfCharacters={250}
              />
            )}
          </VStack>
        )}

      {dayjs().isSameOrBefore(eventData.endDate) && (
        <VStack
          spacing="4"
          w="full"
          justifyContent="flex-start"
          alignItems="flex-start"
          shadow="md"
          p="4"
        >
          <DeprecatedDialInInfo {...dialInProps} />
        </VStack>
      )}

      {resourcesCount > 0 && (
        <VStack
          spacing="4"
          w="full"
          justifyContent="flex-start"
          alignItems="flex-start"
          p="4"
        >
          <HStack w="full" justify="space-between">
            <Text fontWeight="semibold">Resources</Text>
            <Button
              isLoading={resourcesLoading}
              onClick={() => refetchResources()}
            >
              Refresh
            </Button>
          </HStack>
          {allResources?.slice(0, numfilesToShow).map((resource) => {
            return (
              <ResourceCard
                key={resource._id}
                resource={resource}
                canUserEdit={false}
                onRemoveResource={null}
                isRemoving={false}
              />
            );
          })}

          {numfilesToShow < resourcesCount && (
            <Center py="4" w="full">
              <Button
                variant="link"
                colorScheme="black"
                onClick={() =>
                  setNumfilesToShow(
                    (numfilesToShow) => numfilesToShow + INCREMENT_NUM
                  )
                }
              >
                See More Documents
              </Button>
            </Center>
          )}
        </VStack>
      )}
    </VStack>
  );
};

const RESOURCES_QUERY = gql`
  query MobileEventInfo_Resources($input: EventInput!) {
    event(input: $input) {
      eventV2 {
        _id
        ... on SimpleEventV2 {
          _id
          resources(input: { offset: 0, limit: 100 }) {
            results {
              _id
              authorId
              dateCreated
              resourceSpec {
                ... on EventResource_Spec_Document {
                  file {
                    name
                    downloadUrl
                    contentType
                  }
                }
              }
            }
            totalCount
          }
        }
      }
    }
  }
`;

SimpleEventMobileInfo.eventFragment = gql`
  fragment SimpleEventMobileInfo_EventFragment on EventV2 {
    _id
    registrationType
    currentValidUserRegistration {
      _id
    }
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
    name
    startDate
    endDate
    description

    ... on SimpleEventV2 {
      _id
      canCurrentUserJoinEventEarly
      isCurrentUserSpeaker
      speakers {
        isHidden
        user {
          _id
          ...SpeakerCard_UserFragment
        }
      }
      venue {
        ...VenueFragment
      }
    }
    ...EventAudience_EventFragment
  }
  ${EventAudience.eventFragment}
  ${SpeakerCard.userFragment}
  ${VenueFragment}
`;
